import React, { useCallback, useEffect, useState } from 'react';
import { ComboItem, getComboItems } from '../utils/comboItems';
import ComboModal from './ComboModal';
import ImageLightbox from './ImageLightbox';

interface Props {
    itemName?: string,
    imageSrc?: string[],
    description?: string,
    smallPrice?: string,
    regularPrice?: string,
    largePrice?: string,
    isCombo?: boolean
}

interface SizePrice {
    size: string,
    price: string
}

const PREVIEW_COUNT = 4;

const formatPrice = (price: string): string => {
    const num = parseFloat(price);
    return isNaN(num) ? price : `$${num.toFixed(2)}`;
};

// Descriptions containing a literal "\n" are rendered as a bullet list.
const parseDescriptionAsList = (desc: string): string[] | null => {
    if (!desc.includes('\\n')) return null;
    return desc.split('\\n').map(item => item.trim()).filter(item => item.length > 0);
};

const getPriceDisplay = (smallPrice: string, regularPrice: string, largePrice: string): string | SizePrice[] => {
    if (smallPrice && largePrice && smallPrice !== largePrice) {
        const prices = [{ size: 'S', price: formatPrice(smallPrice) }];
        if (regularPrice && regularPrice !== smallPrice && regularPrice !== largePrice) {
            prices.push({ size: 'M', price: formatPrice(regularPrice) });
        }
        prices.push({ size: 'L', price: formatPrice(largePrice) });
        return prices;
    }
    if (regularPrice) return formatPrice(regularPrice);
    if (smallPrice) return formatPrice(smallPrice);
    return [];
};

function ItemDisplay({
    itemName='Default Name',
    imageSrc=[],
    description='',
    smallPrice='',
    regularPrice='',
    largePrice='',
    isCombo=false
}: Props) {

    const [comboItems, setComboItems] = useState<ComboItem[]>([]);
    const [isComboModalOpen, setIsComboModalOpen] = useState(false);
    const [isImageExpanded, setIsImageExpanded] = useState(false);

    useEffect(() => {
        if (!isCombo) return;
        let cancelled = false;
        getComboItems(itemName)
            .then(items => { if (!cancelled) setComboItems(items); })
            .catch(console.error);
        return () => { cancelled = true; };
    }, [isCombo, itemName]);

    const openComboModal = () => setIsComboModalOpen(true);
    const closeComboModal = useCallback(() => setIsComboModalOpen(false), []);
    const closeImage = useCallback(() => setIsImageExpanded(false), []);

    const descriptionItems = parseDescriptionAsList(description);
    const priceDisplay = getPriceDisplay(smallPrice, regularPrice, largePrice);

    const renderComboPreview = () => {
        const remaining = Math.max(comboItems.length - PREVIEW_COUNT, 0);
        return (
            <div className="item-image-container combo-preview">
                <div className="combo-preview-grid">
                    {Array.from({ length: PREVIEW_COUNT }).map((_, i) => {
                        const item = comboItems[i];
                        const url = item?.url || imageSrc[i] || imageSrc[0];
                        if (!url) return <div key={i} />;
                        return (
                            <div key={i} className="combo-preview-cell">
                                <img src={url} alt={item?.name || `${itemName} component ${i + 1}`} />
                                {item && item.quantity > 1 && <span className="quantity-badge">x{item.quantity}</span>}
                                {i === PREVIEW_COUNT - 1 && remaining > 0 && (
                                    <div className="combo-preview-more">+{remaining} more</div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    return (
        <>
        <div
            className={`item-card ${isCombo ? 'is-combo' : 'not-combo'}`}
            onClick={isCombo ? openComboModal : undefined}
            role={isCombo ? 'button' : undefined}
            tabIndex={isCombo ? 0 : undefined}
            onKeyDown={isCombo ? (e) => { if (e.key === 'Enter' || e.key === ' ') openComboModal(); } : undefined}
        >
            {isCombo ? renderComboPreview() : (
                imageSrc.length > 0 && (
                    <button
                        className="item-image-container expandable-image"
                        onClick={() => setIsImageExpanded(true)}
                        aria-label={`Expand image of ${itemName}`}
                    >
                        <img src={imageSrc[0]} alt={itemName} className="item-image" />
                    </button>
                )
            )}
            <div className="menu-card-body">
                <h5 className="menu-card-title">{itemName}</h5>
                {description && (
                    descriptionItems ? (
                        <ul className="menu-card-list">
                            {descriptionItems.map((item, idx) => <li key={idx}>{item}</li>)}
                        </ul>
                    ) : (
                        <p className="menu-card-text">{description}</p>
                    )
                )}
                {Array.isArray(priceDisplay) ? (
                    <div className="price-with-sizes">
                        {priceDisplay.map(item => (
                            <div key={item.size} className="size-price-item">
                                <span className="size-label">{item.size}</span>
                                <span className="size-price">{item.price}</span>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="price-text">{priceDisplay}</p>
                )}
            </div>
        </div>

        {isComboModalOpen && <ComboModal items={comboItems} onClose={closeComboModal} />}
        {isImageExpanded && <ImageLightbox src={imageSrc[0]} alt={itemName} onClose={closeImage} />}
        </>
    );
}

export default ItemDisplay;
